import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, BookOpen, Check } from "react-feather";
import {
  fullStackWebDev,
  fullStackAppDev,
  manualTesting,
  toscaAutomation,
  awsDevOps,
} from "../data/curriculumData";

function TopicList({ items, color }) {
  return (
    <ul className="space-y-2 mt-3">
      {items.map((topic, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm text-white/65 leading-relaxed">
          <Check size={14} className="flex-shrink-0 mt-0.5" style={{ color }} />
          <span>{topic}</span>
        </li>
      ))}
    </ul>
  );
}

function SectionBlock({ section, color }) {
  return (
    <div
      className="curriculum-module h-full rounded-xl p-4 border border-white/8"
      style={{ borderLeftWidth: 3, borderLeftColor: color }}
    >
      <h4 className="text-sm font-bold text-white leading-snug">{section.title}</h4>
      {section.topics && <TopicList items={section.topics} color={color} />}
      {section.subsections && (
        <div className="mt-3 space-y-4">
          {section.subsections.map((sub, j) => (
            <div key={j}>
              <p className="text-xs font-semibold uppercase tracking-wide text-white/50 mb-2">{sub.title}</p>
              <TopicList items={sub.items} color={color} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CurriculumAccordion({ course, isOpen, onToggle }) {
  const color = course.color || "#2563EB";

  return (
    <div
      className={`curriculum-card-v2 rounded-2xl overflow-hidden transition-all duration-300 ${
        isOpen ? "curriculum-card-v2--open" : ""
      }`}
      style={{ "--course-color": color }}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 p-5 sm:p-6 text-left curriculum-card-header"
        aria-expanded={isOpen}
      >
        <span className="flex items-center gap-4 min-w-0">
          <span
            className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-lg"
            style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
          >
            <BookOpen size={22} />
          </span>
          <span className="min-w-0">
            <span className="block text-base sm:text-lg font-bold text-white truncate">{course.title}</span>
            {course.subtitle && (
              <span className="block text-sm text-white/50 mt-0.5 truncate">{course.subtitle}</span>
            )}
          </span>
        </span>
        <span
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
            isOpen ? "curriculum-chevron-open" : "bg-white/5 text-white/70"
          }`}
          style={isOpen ? { background: `${color}25`, color } : undefined}
        >
          <ChevronDown
            size={20}
            className={`transition-transform duration-300 ${isOpen ? "rotate-0" : "-rotate-90"}`}
          />
        </span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="curriculum-card-body px-5 sm:px-6 pb-6 pt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {course.sections.map((section, idx) => (
                  <SectionBlock key={idx} section={section} color={color} />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function CurriculumSection({ defaultExpanded = null }) {
  const [openCourse, setOpenCourse] = useState(defaultExpanded);

  useEffect(() => {
    if (defaultExpanded) setOpenCourse(defaultExpanded);
  }, [defaultExpanded]);

  const courses = [
    { id: "fullstack", ...fullStackWebDev },
    { id: "fullstackapp", ...fullStackAppDev },
    { id: "manual", ...manualTesting },
    { id: "tosca", ...toscaAutomation },
    { id: "aws", ...awsDevOps },
  ];

  return (
    <section className="curriculum-premium py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 lg:mb-14"
        >
          <span className="premium-badge mb-4">Detailed Syllabus</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight">
            Course Curriculum
          </h2>
          <p className="mt-4 text-white/55 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
            Select a course to explore modules, topics, and the complete learning path.
          </p>
        </motion.div>

        <div className="space-y-4">
          {courses.map((course, i) => (
            <motion.div
              key={course.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
            >
              <CurriculumAccordion
                course={course}
                isOpen={openCourse === course.id}
                onToggle={() => setOpenCourse(openCourse === course.id ? null : course.id)}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
